import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { format } from "date-fns";
import axios from "axios";
import { 
  Car, MapPin, User, Phone, Mail, Plane, Calendar as CalendarIcon,
  Clock, Users, DollarSign, Loader2, Info, Navigation, CheckCircle,
  Baby, Briefcase, Plus, Check, CreditCard, X, RefreshCw
} from "lucide-react";
import AddressAutocomplete from "../components/AddressAutocomplete";
import SortableStopItem from "../components/SortableStopItem";
import RouteMapPreview from "../components/RouteMapPreview";
import PageTransition from "../components/PageTransition";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BACKEND_URL}/api` });

const GuestBookingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [calculatingDistance, setCalculatingDistance] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [extras, setExtras] = useState([]);
  
  // Initialize date/time from URL params
  const [date, setDate] = useState(() => {
    const urlDate = searchParams.get("date");
    if (urlDate) {
      try {
        return new Date(urlDate + "T12:00:00");
      } catch {
        return new Date();
      }
    }
    return new Date();
  });
  
  const [time, setTime] = useState(() => {
    return searchParams.get("time") || "09:00";
  });
  
  const [distanceInfo, setDistanceInfo] = useState(null);
  const [priceQuote, setPriceQuote] = useState(null);
  const [allVehiclePrices, setAllVehiclePrices] = useState({}); // Prices for all vehicles
  const [manualDistance, setManualDistance] = useState("");
  const [distanceError, setDistanceError] = useState("");
  const [bookingSuccess, setBookingSuccess] = useState(null);
  const [quoteRequestSuccess, setQuoteRequestSuccess] = useState(null);
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [pickupSelected, setPickupSelected] = useState(false);
  const [dropoffSelected, setDropoffSelected] = useState(false);
  
  const [formData, setFormData] = useState({
    pickup_location: searchParams.get("pickup") || "",
    dropoff_location: searchParams.get("dropoff") || "",
    passenger_name: "",
    passenger_email: "",
    passenger_phone: "",
    num_passengers: 1,
    vehicle_type: "executive_sedan",
    booking_type: "transfer",
    hours: 2,
    flight_number: "",
    special_requirements: "",
    notes: "",
    price: 0
  });
  const [stops, setStops] = useState([]);
  const [stopIds, setStopIds] = useState([]); // Stable IDs for drag-and-drop
  const stopsKey = JSON.stringify(stops);

  // Local pickup datetime string for surcharge calculation (night/festive)
  const getPickupLocalISO = () => {
    if (!date || !time) return null;
    const d = new Date(date);
    const [h, m] = time.split(":");
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}T${h}:${m}:00`;
  };

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = stopIds.indexOf(active.id);
      const newIndex = stopIds.indexOf(over.id);
      setStops(arrayMove(stops, oldIndex, newIndex));
      setStopIds(arrayMove(stopIds, oldIndex, newIndex));
    }
  };

  const addStop = () => {
    setStops([...stops, ""]);
    setStopIds([...stopIds, `stop-${Date.now()}`]);
  };

  const removeStop = (index) => {
    setStops(stops.filter((_, i) => i !== index));
    setStopIds(stopIds.filter((_, i) => i !== index));
  };

  const updateStop = (index, value) => {
    const newStops = [...stops];
    newStops[index] = value;
    setStops(newStops);
  };

  useEffect(() => {
    fetchPricing();
    fetchExtras();
  }, []);

  // Auto-calculate distance when page loads with URL parameters
  useEffect(() => {
    const urlPickup = searchParams.get("pickup");
    const urlDropoff = searchParams.get("dropoff");
    
    if (urlPickup && urlDropoff && pricing && formData.booking_type === "transfer") {
      console.log("Auto-calculating distance for URL params:", urlPickup, urlDropoff);
      // Pass the URL params directly to avoid stale closure
      calculateDistance(decodeURIComponent(urlPickup), decodeURIComponent(urlDropoff));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricing]); // Only trigger when pricing loads

  // Auto-calculate distance when both addresses are selected from autocomplete
  useEffect(() => {
    if (pickupSelected && dropoffSelected && formData.booking_type === "transfer") {
      calculateDistance();
      setPickupSelected(false);
      setDropoffSelected(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupSelected, dropoffSelected, formData.booking_type]);

  const fetchPricing = async () => {
    try {
      const response = await api.get("/vehicles/pricing");
      setPricing(response.data);
    } catch (error) {
      console.error("Failed to load pricing");
    }
  };

  const fetchExtras = async () => {
    try {
      const response = await api.get("/extras");
      setExtras(response.data);
    } catch (error) {
      console.error("Failed to load extras");
    }
  };

  const calculateDistance = useCallback(async (overridePickup = null, overrideDropoff = null) => {
    const pickup = overridePickup || formData.pickup_location;
    const dropoff = overrideDropoff || formData.dropoff_location;
    
    if (!pickup || !dropoff || formData.booking_type === "hourly") {
      return;
    }
    
    setCalculatingDistance(true);
    setDistanceError("");
    try {
      const filteredStops = stops.filter(s => s && s.trim() !== "");
      const payload = { origin: pickup, destination: dropoff };
      if (filteredStops.length > 0) {
        payload.waypoints = filteredStops;
      }
      const response = await api.post("/distance/calculate", payload);
      setDistanceInfo(response.data);
      setManualDistance(response.data.distance_km.toString());
      await calculatePrice(response.data.distance_km);
      await calculateAllVehiclePricesWithFetch(response.data.distance_km, pickup, dropoff);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Distance calculation not available";
      setDistanceError(errorMsg);
      setDistanceInfo(null);
      toast.error("Auto-calculation unavailable. Please enter distance manually.");
    } finally {
      setCalculatingDistance(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.pickup_location, formData.dropoff_location, formData.booking_type, stops]);

  const handleManualDistanceChange = (value) => {
    setManualDistance(value);
    const km = parseFloat(value) || 0;
    if (km > 0) {
      calculatePrice(km);
      calculateAllVehiclePricesWithFetch(km, formData.pickup_location, formData.dropoff_location);
    }
  };

  // Calculate prices for ALL vehicles - with fresh pricing fetch
  const calculateAllVehiclePricesWithFetch = async (distanceKm, pickup, dropoff) => {
    try {
      // First get fresh pricing data
      const pricingResponse = await api.get("/vehicles/pricing");
      const freshPricing = pricingResponse.data;
      
      if (!freshPricing || Object.keys(freshPricing).length === 0) {
        console.log("Cannot calculate all vehicle prices - no pricing data");
        return;
      }
      
      const prices = {};
      const vehicleSlugs = Object.keys(freshPricing);
      const isHourly = formData.booking_type === "hourly";
      
      if (isHourly) {
        // For hourly bookings, calculate via API to include stop surcharge
        const hours = formData.hours || 2;
        const numStops = stops.length; // Count all stops for instant price feedback
        
        await Promise.all(vehicleSlugs.map(async (slug) => {
          try {
            const vehicle = freshPricing[slug];
            const minHours = vehicle?.min_hours || 2;
            const effectiveHours = Math.max(hours, minHours);
            const response = await api.post("/pricing/quote", {
              origin: pickup || "N/A",
              destination: dropoff || "As directed",
              vehicle_type_slug: slug,
              booking_type: "hourly",
              hours: effectiveHours,
              num_stops: numStops,
              pickup_datetime: getPickupLocalISO(),
              public: true
            });
            prices[slug] = response.data.price;
          } catch (error) {
            const vehicle = freshPricing[slug];
            if (vehicle) {
              const minHours = vehicle.min_hours || 2;
              const effectiveHours = Math.max(hours, minHours);
              prices[slug] = (vehicle.hourly_rate * effectiveHours) + (numStops * 20);
            }
          }
        }));
        setAllVehiclePrices(prices);
      } else {
        // For transfers, need distance
        if (!distanceKm) {
          console.log("Cannot calculate transfer prices - missing distance");
          return;
        }
        console.log("Calculating prices for vehicles:", vehicleSlugs, "with distance:", distanceKm);
        
        await Promise.all(vehicleSlugs.map(async (slug) => {
          try {
            const numStops = stops.length; // Count all stops for instant price feedback
            const requestData = {
              origin: pickup || "N/A",
              destination: dropoff || "As directed",
              vehicle_type_slug: slug,
              booking_type: "transfer",
              distance_km: distanceKm,
              hours: null,
              num_stops: numStops,
              pickup_datetime: getPickupLocalISO(),
              public: true
            };
            
            const response = await api.post("/pricing/quote", requestData);
            prices[slug] = response.data.price_type === "quote_required" ? null : response.data.price;
          } catch (error) {
            prices[slug] = null;
          }
        }));
        
        setAllVehiclePrices(prices);
      }
    } catch (error) {
      console.error("Failed to calculate all vehicle prices:", error);
    }
  };

  const calculatePrice = async (distanceKm = null) => {
    try {
      const numStops = stops.length; // Count all stops for instant price feedback
      const requestData = {
        origin: formData.pickup_location || "N/A",
        destination: formData.dropoff_location || "As directed",
        vehicle_type_slug: formData.vehicle_type,
        booking_type: formData.booking_type,
        distance_km: distanceKm || parseFloat(manualDistance) || distanceInfo?.distance_km || 0,
        hours: formData.booking_type === "hourly" ? formData.hours : null,
        num_stops: numStops,
        pickup_datetime: getPickupLocalISO(),
        public: true
      };
      
      const response = await api.post("/pricing/quote", requestData);
      setPriceQuote(response.data);
      const newPrice = response.data.price_type === "quote_required" ? 0 : response.data.price;
      setFormData(prev => ({ ...prev, price: newPrice }));
    } catch (error) {
      console.error("Failed to calculate price");
    }
  };

  useEffect(() => {
    if (pricing && formData.vehicle_type) {
      calculatePrice();
    }
    // Recalculate all vehicle prices when hours, booking type, or stops change
    if (pricing) {
      calculateAllVehiclePricesWithFetch(
        distanceInfo?.distance_km || 0,
        formData.pickup_location,
        formData.dropoff_location
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.vehicle_type, formData.booking_type, formData.hours, stopsKey, date, time]);

  // Auto-recalculate distance when stops content changes (for transfers)
  useEffect(() => {
    if (formData.booking_type === "transfer" && formData.pickup_location && formData.dropoff_location) {
      // Only recalculate if there are actual stop addresses (not empty strings)
      const hasContent = stops.some(s => s && s.trim() !== "");
      if (hasContent || stops.length === 0) {
        calculateDistance();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopsKey]);

  const handlePickupSelect = (value) => {
    setFormData(prev => ({ ...prev, pickup_location: value }));
    setPickupSelected(true);
  };

  const handleDropoffSelect = (value) => {
    setFormData(prev => ({ ...prev, dropoff_location: value }));
    setDropoffSelected(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleExtraToggle = (extraId) => {
    setSelectedExtras(prev => 
      prev.includes(extraId) 
        ? prev.filter(id => id !== extraId)
        : [...prev, extraId]
    );
  };

  const getExtrasTotal = () => {
    return selectedExtras.reduce((total, extraId) => {
      const extra = extras.find(e => e.id === extraId);
      return total + (extra?.price || 0);
    }, 0);
  };

  const getTotalPrice = () => {
    return (formData.price || 0) + getExtrasTotal();
  };

  // Calculate card fee (2.2%)
  const getCardFee = () => {
    return Math.round(getTotalPrice() * 0.022 * 100) / 100;
  };

  const getTotalWithFee = () => {
    return getTotalPrice() + getCardFee();
  };

  const quoteRequired = formData.booking_type === "transfer" && priceQuote?.price_type === "quote_required";

  const handleQuoteRequest = async () => {
    setLoading(true);
    try {
      const bookingDate = new Date(date);
      const [hours, minutes] = time.split(":");
      bookingDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      const filteredStops = stops.filter(s => s && s.trim() !== "");
      const response = await api.post("/quotes/request", {
        pickup_location: formData.pickup_location,
        dropoff_location: formData.dropoff_location,
        pickup_datetime: bookingDate.toISOString(),
        passenger_name: formData.passenger_name,
        passenger_email: formData.passenger_email,
        passenger_phone: formData.passenger_phone,
        num_passengers: formData.num_passengers,
        vehicle_type: formData.vehicle_type,
        booking_type: formData.booking_type,
        hours: formData.booking_type === "hourly" ? formData.hours : null,
        distance_km: parseFloat(manualDistance) || distanceInfo?.distance_km || null,
        flight_number: formData.flight_number || null,
        special_requirements: formData.special_requirements || null,
        stops: filteredStops
      });
      setQuoteRequestSuccess(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send quote request");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.pickup_location) {
      toast.error("Please enter pickup location");
      return;
    }

    if (formData.booking_type === "transfer" && !formData.dropoff_location) {
      toast.error("Please enter dropoff location for transfer booking");
      return;
    }

    if (!formData.passenger_name || !formData.passenger_email || !formData.passenger_phone) {
      toast.error("Please enter your contact details");
      return;
    }

    if (quoteRequired) {
      await handleQuoteRequest();
      return;
    }

    if (getTotalPrice() <= 0) {
      toast.error("Please calculate the price first");
      return;
    }

    setLoading(true);

    try {
      const bookingDate = new Date(date);
      const [hours, minutes] = time.split(":");
      bookingDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

      const filteredStops = stops.filter(s => s && s.trim() !== "");
      
      // Create booking and get Stripe checkout URL
      const response = await api.post("/bookings/guest/checkout", {
        ...formData,
        stops: filteredStops,
        pickup_datetime: bookingDate.toISOString(),
        distance_km: parseFloat(manualDistance) || distanceInfo?.distance_km || null,
        extras: selectedExtras,
        extras_total: getExtrasTotal(),
        origin_url: window.location.origin
      });

      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        toast.success("Redirecting to payment...");
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error("No checkout URL returned");
      }

    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to create booking";
      toast.error(errorMsg);
      setLoading(false);
    }
  };

  if (quoteRequestSuccess) {
    return (
      <div className="min-h-screen bg-background">
        <header className="glass sticky top-0 z-10 border-b border-white/5">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/limo-logo.jpg" alt="Limo Near Me" className="h-10" />
            </Link>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-4 py-16">
          <Card className="dashboard-card text-center" data-testid="quote-request-success">
            <CardContent className="pt-8 pb-8">
              <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
                <Mail className="h-10 w-10 text-amber-600" />
              </div>
              <h1 className="text-3xl font-bold mb-2">Quote Request Received!</h1>
              <p className="text-muted-foreground mb-6">Your reference is:</p>
              <div className="text-4xl font-mono font-bold text-primary mb-6">
                {quoteRequestSuccess.quote_ref}
              </div>
              <p className="text-sm text-muted-foreground mb-8 max-w-md mx-auto">
                This route needs a custom price. Our team is on it — we'll email your fixed quote
                with a secure payment link, usually within a few hours. Need it faster? Call or WhatsApp us on
                <a href="tel:+61481437772" className="text-primary font-semibold"> +61 481 437 772</a>.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="https://wa.me/61481437772" target="_blank" rel="noreferrer">
                  <Button className="bg-green-600 hover:bg-green-700 text-white" data-testid="quote-success-whatsapp-btn">WhatsApp Us</Button>
                </a>
                <Link to="/"><Button variant="outline" data-testid="quote-success-home-btn">Back to Home</Button></Link>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (bookingSuccess) {
    return (
      <div className="min-h-screen bg-background">
        <header className="glass sticky top-0 z-10 border-b border-white/5">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/limo-logo.jpg" alt="Limo Near Me" className="h-10" />
            </Link>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-16">
          <Card className="dashboard-card text-center" data-testid="booking-success">
            <CardContent className="pt-8 pb-8">
              <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-500" />
              </div>
              <h1 className="text-3xl font-bold mb-2">Booking Confirmed!</h1>
              <p className="text-muted-foreground mb-6">
                Your booking reference is:
              </p>
              <div className="text-4xl font-mono font-bold text-primary mb-6">
                {bookingSuccess.booking_ref}
              </div>
              <p className="text-sm text-muted-foreground mb-8">
                A confirmation email has been sent to <span className="text-foreground">{bookingSuccess.passenger_email}</span>
              </p>

              <div className="bg-black/5 rounded-lg p-6 text-left mb-8">
                <h3 className="font-semibold mb-4">Booking Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Pickup:</span>
                    <span className="text-right max-w-[200px] truncate">{bookingSuccess.pickup_location}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Dropoff:</span>
                    <span className="text-right max-w-[200px] truncate">{bookingSuccess.dropoff_location}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Date & Time:</span>
                    <span>{new Date(bookingSuccess.pickup_datetime).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Vehicle:</span>
                    <span className="capitalize">{bookingSuccess.vehicle_type}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg pt-2 border-t border-black/10">
                    <span>Total:</span>
                    <span className="text-green-500">${bookingSuccess.price?.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/login">
                  <Button variant="outline" data-testid="login-btn">
                    Login to Manage Booking
                  </Button>
                </Link>
                <Link to="/">
                  <Button data-testid="home-btn">
                    Back to Home
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <PageTransition>
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass sticky top-0 z-10 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/limo-logo.jpg" alt="Limo Near Me" className="h-10" />
          </Link>
          <Link to="/login">
            <Button variant="outline" size="sm" data-testid="login-link">
              Login
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Book Your Transfer</h1>
          <p className="text-muted-foreground">
            Get an instant quote and book your ride in minutes
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Trip Details */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                Trip Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Booking Type Toggle */}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={formData.booking_type === "transfer" ? "default" : "outline"}
                  onClick={() => setFormData(prev => ({ ...prev, booking_type: "transfer" }))}
                  className="flex-1"
                  data-testid="booking-type-transfer"
                >
                  Point to Point
                </Button>
                <Button
                  type="button"
                  variant={formData.booking_type === "hourly" ? "default" : "outline"}
                  onClick={() => setFormData(prev => ({ ...prev, booking_type: "hourly" }))}
                  className="flex-1"
                  data-testid="booking-type-hourly"
                >
                  Hourly Hire
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Pickup Location *</Label>
                  <AddressAutocomplete
                    value={formData.pickup_location}
                    onChange={(value) => setFormData(prev => ({ ...prev, pickup_location: value }))}
                    onSuggestionSelect={handlePickupSelect}
                    placeholder="Enter pickup address"
                    data-testid="pickup-input"
                  />
                </div>
                
                {formData.booking_type === "transfer" && (
                  <div className="space-y-2">
                    <Label>Dropoff Location *</Label>
                    <AddressAutocomplete
                      value={formData.dropoff_location}
                      onChange={(value) => setFormData(prev => ({ ...prev, dropoff_location: value }))}
                      onSuggestionSelect={handleDropoffSelect}
                      placeholder="Enter dropoff address"
                      data-testid="dropoff-input"
                    />
                  </div>
                )}

                {formData.booking_type === "hourly" && (
                  <div className="space-y-2">
                    <Label>Duration (Hours)</Label>
                    <Select value={formData.hours.toString()} onValueChange={(v) => setFormData(prev => ({ ...prev, hours: parseInt(v) }))}>
                      <SelectTrigger data-testid="hours-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[2, 3, 4, 5, 6, 7, 8, 10, 12].map(h => (
                          <SelectItem key={h} value={h.toString()}>{h} hours</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Via Stops */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2 text-sm">
                    <span className="w-5 h-5 rounded-full bg-yellow-500/20 flex items-center justify-center text-xs text-yellow-500">+</span>
                    Add Stops (Optional)
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addStop}
                    className="text-xs"
                    data-testid="add-stop-btn"
                  >
                    + Add Stop
                  </Button>
                </div>
                
                {stops.length > 0 && (
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={stopIds} strategy={verticalListSortingStrategy}>
                      <div className="space-y-2">
                        {stops.map((stop, index) => (
                          <SortableStopItem
                            key={stopIds[index]}
                            id={stopIds[index]}
                            index={index}
                            stop={stop}
                            onUpdate={updateStop}
                            onRemove={removeStop}
                            onSelect={(idx, value) => updateStop(idx, value)}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
              </div>

              {/* Distance Info */}
              {calculatingDistance && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Calculating distance...
                </div>
              )}

              {distanceInfo && (
                <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                  <div className="flex items-center gap-2 text-green-500">
                    <Navigation className="h-4 w-4" />
                    <span className="font-medium">{distanceInfo.distance_km} km</span>
                    <span className="text-muted-foreground">• Estimated {distanceInfo.duration_text}</span>
                  </div>
                </div>
              )}

              {distanceError && formData.booking_type === "transfer" && (
                <div className="space-y-2">
                  <Label>Distance (km) - Enter manually</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={manualDistance}
                    onChange={(e) => handleManualDistanceChange(e.target.value)}
                    placeholder="e.g., 25"
                    data-testid="manual-distance"
                  />
                </div>
              )}

              {/* Route Map Preview */}
              <RouteMapPreview
                pickup={formData.pickup_location}
                dropoff={formData.dropoff_location}
                stops={stops}
              />

              {/* Date and Time */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Pickup Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start" data-testid="date-picker">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(date, "PPP")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={(d) => d && setDate(d)}
                        disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>Pickup Time *</Label>
                  <Input
                    type="time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    data-testid="time-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Vehicle Selection */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Car className="h-5 w-5 text-primary" />
                Select Vehicle
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {pricing && Object.keys(pricing)
                  .sort((a, b) => (pricing[a]?.base_price || 0) - (pricing[b]?.base_price || 0))
                  .map(vehicleSlug => {
                  const vehicle = pricing[vehicleSlug];
                  const calculatedPrice = allVehiclePrices[vehicleSlug];
                  const isHourly = formData.booking_type === "hourly";
                  const hasCalculatedPrice = calculatedPrice !== undefined && calculatedPrice !== null && (distanceInfo || isHourly);
                  
                  return (
                    <button
                      key={vehicleSlug}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, vehicle_type: vehicleSlug }))}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        formData.vehicle_type === vehicleSlug
                          ? "border-primary bg-primary/10"
                          : "border-black/10 hover:border-primary/30"
                      }`}
                      data-testid={`vehicle-${vehicleSlug}`}
                    >
                      {vehicle?.image_url ? (
                        <img 
                          src={vehicle.image_url} 
                          alt={vehicle.name || vehicleSlug} 
                          className="h-16 w-auto mx-auto mb-2 object-contain"
                        />
                      ) : (
                        <Car className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                      )}
                      <p className="font-medium text-sm">{vehicle?.name || vehicleSlug}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {vehicle?.capacity && `Up to ${vehicle.capacity} passengers`}
                      </p>
                      {hasCalculatedPrice ? (
                        <p className="text-lg font-bold text-primary mt-1">
                          ${calculatedPrice.toFixed(2)}
                        </p>
                      ) : isHourly ? (
                        <p className="text-sm font-bold text-primary mt-1">
                          ${vehicle?.hourly_rate}/hr
                        </p>
                      ) : calculatedPrice === null && distanceInfo ? (
                        <p className="text-xs font-semibold text-amber-600 mt-1" data-testid={`quote-only-${vehicleSlug}`}>
                          Quote on request
                        </p>
                      ) : null}
                    </button>
                  );
                })}
              </div>

              <div className="mt-4">
                <Label>Number of Passengers</Label>
                <Select value={formData.num_passengers.toString()} onValueChange={(v) => setFormData(prev => ({ ...prev, num_passengers: parseInt(v) }))}>
                  <SelectTrigger className="mt-2" data-testid="passengers-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => (
                      <SelectItem key={n} value={n.toString()}>{n} {n === 1 ? "passenger" : "passengers"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Extras */}
          {extras.length > 0 && (
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="h-5 w-5 text-primary" />
                  Add Extras
                </CardTitle>
                <CardDescription>Optional add-ons for your trip</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {extras.map(extra => (
                    <div
                      key={extra.id}
                      onClick={() => handleExtraToggle(extra.id)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedExtras.includes(extra.id)
                          ? "border-primary bg-primary/10"
                          : "border-black/10 hover:border-white/20"
                      }`}
                      data-testid={`extra-${extra.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className={`mt-1 h-4 w-4 rounded-sm border flex items-center justify-center ${
                            selectedExtras.includes(extra.id) 
                              ? "bg-primary border-primary" 
                              : "border-white/30"
                          }`}>
                            {selectedExtras.includes(extra.id) && (
                              <Check className="h-3 w-3 text-primary-foreground" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium">{extra.name}</p>
                            <p className="text-xs text-muted-foreground">{extra.description}</p>
                          </div>
                        </div>
                        <Badge variant="secondary">+${extra.price.toFixed(2)}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Passenger Details */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                Your Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Full Name *</Label>
                  <Input
                    name="passenger_name"
                    value={formData.passenger_name}
                    onChange={handleChange}
                    placeholder="John Smith"
                    data-testid="name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    name="passenger_email"
                    value={formData.passenger_email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    data-testid="email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone *</Label>
                  <Input
                    name="passenger_phone"
                    value={formData.passenger_phone}
                    onChange={handleChange}
                    placeholder="+61 400 000 000"
                    data-testid="phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Flight Number (Optional)</Label>
                  <Input
                    name="flight_number"
                    value={formData.flight_number}
                    onChange={handleChange}
                    placeholder="QF123"
                    data-testid="flight-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Special Requirements (Optional)</Label>
                <Textarea
                  name="special_requirements"
                  value={formData.special_requirements}
                  onChange={handleChange}
                  placeholder="Any special requirements or instructions..."
                  rows={3}
                  data-testid="requirements-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Price Summary */}
          <Card className="dashboard-card border-primary/50">
            <CardContent className="pt-6">
              {quoteRequired ? (
                <div className="space-y-4" data-testid="quote-required-panel">
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200">
                    <Info className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-foreground mb-1">This route needs a custom quote</p>
                      <p className="text-sm text-muted-foreground">
                        We don't have a fixed price for this trip yet. Submit your details below and our team
                        will email you a fixed quote with a secure payment link — usually within a few hours.
                      </p>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    size="lg"
                    disabled={loading}
                    data-testid="submit-quote-request"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Sending Request...
                      </>
                    ) : (
                      <>
                        <Mail className="mr-2 h-5 w-5" />
                        Request a Quote
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-center text-muted-foreground">
                    Prefer to talk? Call or WhatsApp <a href="tel:+61481437772" className="text-primary">+61 481 437 772</a>
                  </p>
                </div>
              ) : (
              <>
              <div className="space-y-3">
                {formData.price > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Base Fare</span>
                    <span>${formData.price.toFixed(2)}</span>
                  </div>
                )}
                {selectedExtras.length > 0 && (
                  <>
                    {selectedExtras.map(extraId => {
                      const extra = extras.find(e => e.id === extraId);
                      return extra ? (
                        <div key={extraId} className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{extra.name}</span>
                          <span>+${extra.price.toFixed(2)}</span>
                        </div>
                      ) : null;
                    })}
                  </>
                )}
                {getTotalPrice() > 0 && (
                  <>
                    <div className="border-t border-black/10 my-2"></div>
                    {priceQuote?.breakdown?.num_stops > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Stop surcharge ({priceQuote.breakdown.num_stops} stop{priceQuote.breakdown.num_stops > 1 ? 's' : ''} x ${priceQuote.breakdown.stop_rate})</span>
                        <span>${priceQuote.breakdown.stop_surcharge.toFixed(2)}</span>
                      </div>
                    )}
                    {priceQuote?.breakdown?.time_surcharge > 0 && (
                      <div className="flex justify-between text-sm" data-testid="time-surcharge-row">
                        <span className="text-muted-foreground">{priceQuote.breakdown.time_surcharge_label}</span>
                        <span>${priceQuote.breakdown.time_surcharge.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span>${getTotalPrice().toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Card Processing Fee (2.2%)</span>
                      <span>${getCardFee().toFixed(2)}</span>
                    </div>
                    <div className="border-t border-black/10 my-2"></div>
                  </>
                )}
                <div className="flex justify-between items-center text-xl font-bold">
                  <span>Total to Pay</span>
                  <span className="text-green-500" data-testid="total-price">
                    ${getTotalWithFee().toFixed(2)}
                  </span>
                </div>
                {priceQuote?.includes_gst && (
                  <p className="text-xs text-muted-foreground text-right">Inc. GST</p>
                )}
              </div>

              <Button 
                type="submit" 
                className="w-full mt-6" 
                size="lg"
                disabled={loading || getTotalPrice() === 0}
                data-testid="submit-booking"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Redirecting to Payment...
                  </>
                ) : (
                  <>
                    <CreditCard className="mr-2 h-5 w-5" />
                    Pay Now - ${getTotalWithFee().toFixed(2)}
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-muted-foreground mt-4">
                Secure payment powered by Stripe
              </p>
              <p className="text-xs text-center text-muted-foreground mt-2">
                Already have an account? <Link to="/login" className="text-primary hover:underline">Login here</Link>
              </p>
              </>
              )}
            </CardContent>
          </Card>
        </form>
      </main>
    </div>
    </PageTransition>
  );
};

export default GuestBookingPage;
